import { requestJson } from '../../../shared/api/http'
import type {
  Source,
  CreateSourceInput,
  UpdateSourceInput,
  SourcesQuery,
} from '../model/contracts'

type SourceDto = {
  id: string
  platform: string
  isEnabled?: boolean
  username?: string
  displayName?: string
  name?: string

  profileUrl?: string
  url?: string

  type?: string
  status?: string
  metadata?: {
    bio?: string
    followingText?: string
    [key: string]: unknown
  }
  description?: string

  createdAt?: string
  updatedAt?: string
  lastCrawled?: string
}

type SourcesResponse = {
  meta: {
    total: number
    page: number
    limit: number
  }
  data: SourceDto[]
}

type FetchSourcesResult = {
  sources: Source[]
  total: number
  page: number
  limit: number
}

function toSource(dto: SourceDto): Source {
  const displayName = dto.displayName || dto.name || dto.username || 'Unknown'
  return {
    id: dto.id,
    name: displayName,
    displayName: dto.displayName,
    username: dto.username,
    platform: dto.platform,
    // Prefer profileUrl, then url
    url: dto.profileUrl || dto.url,
    // Prefer metadata.bio, then description
    description: dto.metadata?.bio || dto.description || '',
    isEnabled: dto.isEnabled ?? true,
    // Use current time if dates are missing
    createdAt: dto.createdAt || new Date().toISOString(),
    updatedAt: dto.updatedAt || dto.lastCrawled || new Date().toISOString(),
  }
}

export async function fetchSources(
  query: SourcesQuery = {},
  signal?: AbortSignal,
): Promise<FetchSourcesResult> {
  const params = new URLSearchParams()
  if (query.page) params.append('page', query.page.toString())
  if (query.limit) params.append('limit', query.limit.toString())
  if (query.platform && query.platform !== 'all')
    params.append('platform', query.platform)
  if (query.isEnabled !== null && query.isEnabled !== undefined) {
    params.append('isEnabled', String(query.isEnabled))
  }
  if (query.createdBy) params.append('createdBy', query.createdBy)
  if (query.search) params.append('search', query.search)
  if (query.sort) params.append('sort', query.sort)

  const suffix = params.toString()
  const response = await requestJson<SourcesResponse>(
    `/api/v1/sources${suffix ? `?${suffix}` : ''}`,
    {
      method: 'GET',
      signal,
    },
  )

  return {
    sources: response.data.map(toSource),
    total: response.meta.total,
    page: response.meta.page,
    limit: response.meta.limit,
  }
}

export async function getSource(id: string, signal?: AbortSignal): Promise<Source> {
  const dto = await requestJson<SourceDto>(`/api/v1/sources/${id}`, {
    method: 'GET',
    signal,
  })
  return toSource(dto)
}

export async function createSource(data: CreateSourceInput): Promise<Source> {
  const dto = await requestJson<SourceDto>('/api/v1/sources', {
    method: 'POST',
    body: JSON.stringify(data),
    headers: { 'Content-Type': 'application/json' },
  })
  return toSource(dto)
}

export async function updateSource(data: UpdateSourceInput): Promise<Source> {
  const { id, ...rest } = data
  const dto = await requestJson<SourceDto>(`/api/v1/sources/${id}`, {
    method: 'PUT',
    body: JSON.stringify(rest),
    headers: { 'Content-Type': 'application/json' },
  })
  return toSource(dto)
}

export async function deleteSource(id: string): Promise<void> {
  return requestJson<void>(`/api/v1/sources/${id}`, { method: 'DELETE' })
}
